import { resolveApiErrorMessage, useApiErrorToast } from 'app/api/app-api/apiHooksHelpers';
import { LoadingSpinner } from "../core/components/LoadingSpinner";
import { UserRole } from 'legoland-sdk/dist/experimental';
import { useEffect, useState } from 'react';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalHeader, ModalLayout, Text, useToasts } from 'tombac';
import { CommunicationMessage } from "./CommunicationMessage";
import { CommunicationTestEmailSender } from "./CommunicationTestEmailSender";
import { useCommunicationEmailRecipientsQuery, useSendCommunicationEmailMutation } from "./communicationsApiHooks";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const SendCommunicationEmailModal = _ref => {
  let {
    communicationItem,
    ...modalProps
  } = _ref;
  const {
    addToast
  } = useToasts();
  const addApiErrorToast = useApiErrorToast();
  const {
    isLoading: isSending,
    mutateAsync: sendEmail
  } = useSendCommunicationEmailMutation();
  const handleSendEmail = async event => {
    try {
      await sendEmail({
        communicationItemId: communicationItem.id
      });
      addToast('Communication email has been sent successfully', 'success', {
        content: 'It may take a few minutes to a few hours for the email to be delivered to all users.'
      });
      modalProps.onRequestClose(event);
    } catch (error) {
      addApiErrorToast(error);
    }
  };
  const {
    error: recipientsError,
    isLoading: isLoadingRecipients,
    data: recipients
  } = useCommunicationEmailRecipientsQuery({
    communicationItemId: communicationItem.id
  });
  const [recipientsErrorMessage, setRecipientsErrorMessage] = useState(null);
  useEffect(() => {
    if (!recipientsError) return;
    resolveApiErrorMessage(recipientsError).then(setRecipientsErrorMessage);
  }, [recipientsError]);
  const usersCount = recipients?.totalElements;
  return /*#__PURE__*/_jsx(Modal, {
    ...modalProps,
    "data-sentry-element": "Modal",
    "data-sentry-component": "SendCommunicationEmailModal",
    "data-sentry-source-file": "SendCommunicationEmailModal.tsx",
    children: /*#__PURE__*/_jsxs(ModalLayout, {
      $width: "900u",
      "data-sentry-element": "ModalLayout",
      "data-sentry-source-file": "SendCommunicationEmailModal.tsx",
      "data-sentry-element": "ModalLayout",
      children: [/*#__PURE__*/_jsx(ModalHeader, {
        "data-sentry-element": "ModalHeader",
        "data-sentry-source-file": "SendCommunicationEmailModal.tsx",
        "data-sentry-element": "ModalHeader",
        children: "Send communication email"
      }), isLoadingRecipients ? /*#__PURE__*/_jsx(ModalContent, {
        $p: "5sp",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ModalContent, {
          children: /*#__PURE__*/_jsxs(Text, {
            $mt: "1sp",
            children: ["The following communication will be sent to", ' ', formatRoles(communicationItem.roles), ":"]
          })
        }), /*#__PURE__*/_jsx(ModalContent, {
          variant: "scrollable",
          children: /*#__PURE__*/_jsx(CommunicationMessage, {
            communicationItem: communicationItem
          })
        }), recipientsError ? /*#__PURE__*/_jsxs(ModalContent, {
          children: [/*#__PURE__*/_jsx(Alert, {
            $mb: "2sp",
            title: "Failed to load the number of users that will receive the communication email.",
            variant: "danger",
            children: recipientsErrorMessage
          }), /*#__PURE__*/_jsxs(Text, {
            children: ["Are you sure you want to send this communication email anyway?", ' ', /*#__PURE__*/_jsx(Text, {
              $display: "inline",
              fontWeight: 700,
              variant: "danger",
              children: "This action cannot be undone!"
            })]
          })]
        }) : usersCount === 0 ? /*#__PURE__*/_jsx(ModalContent, {
          children: /*#__PURE__*/_jsx(Alert, {
            title: "No users would receive the communication email.",
            variant: "alert",
            children: "There are no users with the selected combination of roles and products that have opted in to receive communication emails."
          })
        }) : /*#__PURE__*/_jsx(ModalContent, {
          children: /*#__PURE__*/_jsxs(Text, {
            children: ["Are you sure you want to send this communication email to", ' ', /*#__PURE__*/_jsxs("strong", {
              children: [usersCount, " user", usersCount > 1 && 's']
            }), "?", ' ', /*#__PURE__*/_jsx(Text, {
              $display: "inline",
              fontWeight: 700,
              variant: "danger",
              children: "This action cannot be undone!"
            })]
          })
        })]
      }), /*#__PURE__*/_jsxs(ModalActions, {
        secondary: !isLoadingRecipients && /*#__PURE__*/_jsx(CommunicationTestEmailSender, {
          communicationItem: communicationItem
        }),
        "data-sentry-element": "ModalActions",
        "data-sentry-source-file": "SendCommunicationEmailModal.tsx",
        "data-sentry-element": "ModalActions",
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: modalProps.onRequestClose,
          "data-sentry-element": "Button",
          "data-sentry-source-file": "SendCommunicationEmailModal.tsx",
          "data-sentry-element": "Button",
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          busy: isSending,
          disabled: isLoadingRecipients || usersCount === 0,
          onClick: handleSendEmail,
          variant: "primary",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "SendCommunicationEmailModal.tsx",
          "data-sentry-element": "Button",
          children: "Send email"
        })]
      })]
    })
  });
};
function getUserRoleKeyByValue(value) {
  return Object.keys(UserRole).find(key => UserRole[key] === value);
}
function formatRoles(roles) {
  const roleNames = roles.map(getUserRoleKeyByValue).sort().map(roleName => /*#__PURE__*/_jsx("strong", {
    children: roleName
  }, roleName));
  if (roleNames.length === 1) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: ["all users with the role ", roleNames[0]]
    });
  } else {
    const firstRole = roleNames.shift();
    const lastRole = roleNames.pop();
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: ["all users with the roles ", firstRole, roleNames.reduce((prev, curr) => [...prev, ', ', curr], []), " and", ' ', lastRole]
    });
  }
}